/**
 * Servicio de persistencia usando IndexedDB
 * @module services/storageService
 */

import { AnyNode } from '@/types/nodes'
import { Cable } from '@/types/fibers'
import { Carpeta, Proyecto } from '@/types/folders'

// ============================================================================
// TIPOS
// ============================================================================

export interface StorageData {
  nodes: AnyNode[]
  cables: Cable[]
  folders: Carpeta[]
  projects: Proyecto[]
  fiberConnections: Record<string, string>
  version: string
}

// ============================================================================
// INDEXEDDB MANAGER
// ============================================================================

class StorageService {
  private dbName = 'NetEasyFTTX'
  private dbVersion = 1
  private db: IDBDatabase | null = null

  /**
   * Inicializa la base de datos
   */
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Object stores
        if (!db.objectStoreNames.contains('nodes')) {
          db.createObjectStore('nodes', { keyPath: 'id' })
        }

        if (!db.objectStoreNames.contains('cables')) {
          db.createObjectStore('cables', { keyPath: 'id' })
        }

        if (!db.objectStoreNames.contains('folders')) {
          db.createObjectStore('folders', { keyPath: 'id' })
        }

        if (!db.objectStoreNames.contains('projects')) {
          db.createObjectStore('projects', { keyPath: 'id' })
        }

        if (!db.objectStoreNames.contains('fiberConnections')) {
          db.createObjectStore('fiberConnections')
        }
      }
    })
  }

  /**
   * Asegura que la DB está inicializada
   */
  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init()
    }
    return this.db!
  }

  // ==========================================================================
  // NODOS
  // ==========================================================================

  async saveNode(node: AnyNode): Promise<void> {
    const db = await this.ensureDB()
    const tx = db.transaction('nodes', 'readwrite')
    const store = tx.objectStore('nodes')

    await new Promise<void>((resolve, reject) => {
      const request = store.put(node)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async saveNodes(nodes: AnyNode[]): Promise<void> {
    const db = await this.ensureDB()
    const tx = db.transaction('nodes', 'readwrite')
    const store = tx.objectStore('nodes')

    for (const node of nodes) {
      store.put(node)
    }

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  }

  async getNode(id: string): Promise<AnyNode | undefined> {
    const db = await this.ensureDB()
    const tx = db.transaction('nodes', 'readonly')
    const store = tx.objectStore('nodes')

    return new Promise((resolve, reject) => {
      const request = store.get(id)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async getAllNodes(): Promise<AnyNode[]> {
    const db = await this.ensureDB()
    const tx = db.transaction('nodes', 'readonly')
    const store = tx.objectStore('nodes')

    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  }

  async deleteNode(id: string): Promise<void> {
    const db = await this.ensureDB()
    const tx = db.transaction('nodes', 'readwrite')
    const store = tx.objectStore('nodes')

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  // ==========================================================================
  // CABLES
  // ==========================================================================

  async saveCable(cable: Cable): Promise<void> {
    const db = await this.ensureDB()
    const tx = db.transaction('cables', 'readwrite')
    const store = tx.objectStore('cables')

    await new Promise<void>((resolve, reject) => {
      const request = store.put(cable)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async getAllCables(): Promise<Cable[]> {
    const db = await this.ensureDB()
    const tx = db.transaction('cables', 'readonly')
    const store = tx.objectStore('cables')

    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  }

  async deleteCable(id: string): Promise<void> {
    const db = await this.ensureDB()
    const tx = db.transaction('cables', 'readwrite')
    const store = tx.objectStore('cables')

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  // ==========================================================================
  // CARPETAS
  // ==========================================================================

  async saveFolder(folder: Carpeta): Promise<void> {
    const db = await this.ensureDB()
    const tx = db.transaction('folders', 'readwrite')
    const store = tx.objectStore('folders')

    await new Promise<void>((resolve, reject) => {
      const request = store.put(folder)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async getAllFolders(): Promise<Carpeta[]> {
    const db = await this.ensureDB()
    const tx = db.transaction('folders', 'readonly')
    const store = tx.objectStore('folders')

    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  }

  async deleteFolder(id: string): Promise<void> {
    const db = await this.ensureDB()
    const tx = db.transaction('folders', 'readwrite')
    const store = tx.objectStore('folders')

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  // ==========================================================================
  // PROYECTOS
  // ==========================================================================

  async saveProject(project: Proyecto): Promise<void> {
    const db = await this.ensureDB()
    const tx = db.transaction('projects', 'readwrite')
    const store = tx.objectStore('projects')

    await new Promise<void>((resolve, reject) => {
      const request = store.put(project)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async getAllProjects(): Promise<Proyecto[]> {
    const db = await this.ensureDB()
    const tx = db.transaction('projects', 'readonly')
    const store = tx.objectStore('projects')

    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  }

  // ==========================================================================
  // CONEXIONES DE FIBRA
  // ==========================================================================

  async saveFiberConnections(connections: Record<string, string>): Promise<void> {
    const db = await this.ensureDB()
    const tx = db.transaction('fiberConnections', 'readwrite')
    const store = tx.objectStore('fiberConnections')

    await new Promise<void>((resolve, reject) => {
      const request = store.put(connections, 'connections')
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async getFiberConnections(): Promise<Record<string, string>> {
    const db = await this.ensureDB()
    const tx = db.transaction('fiberConnections', 'readonly')
    const store = tx.objectStore('fiberConnections')

    return new Promise((resolve, reject) => {
      const request = store.get('connections')
      request.onsuccess = () => resolve(request.result || {})
      request.onerror = () => reject(request.error)
    })
  }

  // ==========================================================================
  // OPERACIONES GLOBALES
  // ==========================================================================

  async exportData(): Promise<StorageData> {
    const [nodes, cables, folders, projects, fiberConnections] = await Promise.all([
      this.getAllNodes(),
      this.getAllCables(),
      this.getAllFolders(),
      this.getAllProjects(),
      this.getFiberConnections(),
    ])

    return {
      nodes,
      cables,
      folders,
      projects,
      fiberConnections,
      version: '1.0',
    }
  }

  async importData(data: StorageData): Promise<void> {
    await this.clearAll()

    await Promise.all([
      this.saveNodes(data.nodes),
      ...data.cables.map(c => this.saveCable(c)),
      ...data.folders.map(f => this.saveFolder(f)),
      ...data.projects.map(p => this.saveProject(p)),
      this.saveFiberConnections(data.fiberConnections),
    ])
  }

  async clearAll(): Promise<void> {
    const db = await this.ensureDB()
    const storeNames = ['nodes', 'cables', 'folders', 'projects', 'fiberConnections']

    for (const storeName of storeNames) {
      const tx = db.transaction(storeName, 'readwrite')
      const store = tx.objectStore(storeName)

      await new Promise<void>((resolve, reject) => {
        const request = store.clear()
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    }
  }
}

// Singleton instance
export const storageService = new StorageService()
